import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { IUser } from "@app/interfaces/IUser";
import { DateService, DateUnit } from "@app/services/date.service";
import { combineLatest, forkJoin, Observable, of, Subject } from "rxjs";
import { map, switchMap, take, tap } from "rxjs/operators";
import { IEngineerJobBooking, IEngineerJobBookingWithEngineerMappings } from "../foundation.interfaces";
import { IEngineerJobBookingQueryOptions } from "../_models/IEngineerJobBookingQueryOptions";
import { EngineerJobBookingMappingService } from "./engineer-job-booking-mapping.service";
import { EngineerService } from "./engineer.service";
import { UserGroupService } from "./user-group.service";

interface IAccountSite {
    Id: number;
}

@Injectable({
    providedIn: 'root'
})
export class EngineerJobBookingService {


    selectedJobBookingUpdated = new Subject<IEngineerJobBookingWithEngineerMappings>()
    jobBookings = new Subject<IEngineerJobBookingWithEngineerMappings[]>();

    #selectedJobBooking: IEngineerJobBookingWithEngineerMappings;
    private allJobBookings: IEngineerJobBookingWithEngineerMappings[];

    constructor(
        private readonly http: HttpClient,
        private readonly dateService: DateService,
        private readonly engineerService: EngineerService,
        private readonly engineerJobBookingMappingService: EngineerJobBookingMappingService,
        private readonly userGroupService: UserGroupService) { }

    getEngineeringJobBookings(options: IEngineerJobBookingQueryOptions = {}): Observable<IEngineerJobBooking[]> {
        return this.http.get<IEngineerJobBooking[]>(`/api/foundation/engineerbookings/`, { params: options as HttpParams });
    }

    getEngineeringJobBookingsWithMappings(options: IEngineerJobBookingQueryOptions = {}): Observable<IEngineerJobBookingWithEngineerMappings[]> {
        return combineLatest([
             this.getEngineeringJobBookings(options),
             this.engineerJobBookingMappingService.getEngineerJobBookingMappings(),
             this.engineerService.getEngineers(),
             this.userGroupService.getUserGroups()
        ]).pipe(
            map(([jobBookings, mappings, engineers, userGroups]) => {
                const bookings: IEngineerJobBookingWithEngineerMappings[] = jobBookings;
                bookings.forEach((booking) => {
                    booking.mappings = mappings.filter(mapping => mapping.EngineerJobBookingId === booking.Id);
                    booking.assignedEngineers = [];
                    if (!booking.mappings.length) {
                        return;
                    }
                    if (booking.mappings.some(mapping => !!mapping.UserGroupId)) {
                        const groupId = booking.mappings.find(mapping => !!mapping.UserGroupId).UserGroupId;
                        booking.assignedGroupId = groupId;
                        booking.assignedGroup = userGroups.find(group => group.Id === groupId);
                    }
                    else {
                        booking.mappings.forEach(mapping => {
                            const engineer = engineers.find(eng => eng.Id === mapping.UserId);
                            if (engineer) {
                                booking.assignedEngineers.push(engineer);
                            }
                        })
                    }
                })
                if (!options.JobBookingId && !options.JobId) {
                    this.allJobBookings = bookings;
                    this.jobBookings.next(this.allJobBookings);
                }
                return bookings;
            }))
    }



    createUpdateEngineeringJobBooking(booking: IEngineerJobBookingWithEngineerMappings, accountId: number): Observable<IEngineerJobBooking> {

        return this.http.get<IAccountSite[]>(`/api/foundation/sites/${accountId}`).pipe(
            take(1),
            switchMap((sites: IAccountSite[]) => {
                if (sites && sites.length > 0) {
                    booking.SiteId = sites[0].Id;
                } else {
                    booking.SiteId = 30793;
                }

                booking.Description = "Created for Job Access";

                return this.http.post<IEngineerJobBooking>(`/api/foundation/engineerbookings/`, booking).pipe(tap(newBooking => {
                    booking.Id = newBooking.Id;
                    this.updateAllJobBookings(booking);
                }));
            })
        );
    }

    getAssignedEngineersForJobBooking(jobBookingId): Observable<IUser[]> {
        return combineLatest([
            this.engineerService.getEngineers(),
            this.engineerJobBookingMappingService.getEngineerJobBookingMappings({ EngineerJobBookingId: jobBookingId })
        ]).pipe(map(([users, mappings]) => {
                if (!mappings.length) {
                    return [];
                }

                let engineers = mappings.map(mapping => {
                    return users.find(user => user.Id === mapping.UserId);
                })

                return engineers;

            }))
    }

    getLatestEngineeringJobBookings(): Observable<IEngineerJobBooking[]> {
        const options: IEngineerJobBookingQueryOptions = {
            Start: this.dateService.addOrSubtractTime(new Date(), -28, DateUnit.Day),
            End: this.dateService.addOrSubtractTime(new Date(), 28, DateUnit.Day)
        }
        return this.getEngineeringJobBookings(options);
    }

    getSelectedJobBooking(jobId: number): Observable<IEngineerJobBookingWithEngineerMappings> {
        if (this.#selectedJobBooking?.JobId === jobId) {
            return of(this.#selectedJobBooking);
        }

        return this.getEngineeringJobBookingsWithMappings({ JobId: jobId }).pipe(map(bookings => bookings[0]));
    }

    setSelectedJobBooking(jobBooking: IEngineerJobBookingWithEngineerMappings): void {
        this.#selectedJobBooking = jobBooking;
        this.selectedJobBookingUpdated.next(this.#selectedJobBooking);
    }

    private updateAllJobBookings(booking: IEngineerJobBookingWithEngineerMappings): void {
        const existing = this.allJobBookings.findIndex(bk => bk.Id === booking.Id);
        if (existing < 0) {
            this.allJobBookings.push(booking);
            this.jobBookings.next(this.allJobBookings);
            return;
        }

        this.allJobBookings.splice(existing, 1, booking);
        this.jobBookings.next(this.allJobBookings);
    }
}
